import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import BlogCreate from "./BlogCreate";
import { useAuth } from "../contexts/AuthContext";
import { BlogPost } from "../utils/mediaHelpers";

const BlogEdit: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/blog/${id}`, {
          headers: {
            ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
          },
        });
        if (!res.ok) throw new Error("Failed to fetch blog post");
        const data = await res.json();
        setPost(data);
      } catch (e: any) {
        setError(e.message || "Error loading blog post");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [id, user?.token]);

  const handleUpdate = async (values: Partial<BlogPost>) => {
    if (!user) {
      setError("Not authenticated");
      return;
    }
    setError(null);
    setSuccess(false);
    try {
      // Send data in new BlogPost format with media field
      const response = await fetch(`/api/blog/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({
          title: values.title || post?.title || "",
          content: values.content || post?.content || "",
          category: values.category || post?.category || "News",
          ...(values.media !== undefined && { media: values.media }),
          hidden: false,
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to update blog post");
      }
      setSuccess(true);
      setTimeout(() => navigate(`/blog/${id}`), 1000);
    } catch (err: any) {
      setError(err.message || "Error updating blog post");
    }
  };

  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  if (!post) return null;

  return (
    <Box maxWidth="md" mx="auto" mt={4}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Edit Blog Post
        </Typography>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Blog post updated!
          </Alert>
        )}
        <BlogCreate
          initialValues={post}
          isEdit={true}
          onSuccess={() => {
            setSuccess(true);
            setTimeout(() => navigate(`/blog/${id}`), 1000);
          }}
          onSubmit={handleUpdate}
        />
        <Button sx={{ mt: 2 }} onClick={() => navigate(-1)}>
          Cancel
        </Button>
      </Paper>
    </Box>
  );
};

export default BlogEdit;
